<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import type { CanvasRetrieve } from '@openapi'
import CCard from '@lib/components/CCard/CCard.vue'
import CIcon from '@lib/components/CIcon/CIcon.vue'
import useThemeVars from '@lib/composables/useThemeVars'

interface Props {
  items: CanvasRetrieve[]
  maxItemsToShow?: number
  title?: string
}

const props = withDefaults(defineProps<Props>(), {
  maxItemsToShow: 5,
})

const { t } = useI18n()
const themeVars = useThemeVars()

const itemsToShow = computed(() => props.items.slice(0, props.maxItemsToShow))

function isHome(canvas: CanvasRetrieve): boolean {
  return canvas.tags?.includes('home') ?? false
}
</script>

<template>
  <CCard class="pa-2 c-template-access-info">
    <div class="flex flex-col gap-6">
      <div class="flex items-center gap-4">
        <CIcon icon="presentation-screen" />
        <div class="font-bold">
          {{ title ?? t('canvasUI.components.CTemplateAccessInfo.title', items.length) }}
        </div>
      </div>

      <div class="flex flex-wrap">
        <div
          v-for="(canvas, index) in itemsToShow.slice(0, maxItemsToShow)"
          :key="canvas.id"
          class="flex items-center gap-1"
        >
          <CIcon v-if="isHome(canvas)" :color="themeVars.primary" icon="home" size="small" />
          <span>{{ canvas.name }}</span>
          <span v-if="index < itemsToShow.length - 1">,&nbsp;</span>
        </div>
        <div v-if="items.length > maxItemsToShow" class="ml-2 truncate c-template-access-info__more">
          {{ t('canvasUI.components.CTemplateAccessInfo.more', items.length - maxItemsToShow) }}
        </div>
      </div>
    </div>

    <slot />
  </CCard>
</template>

<style lang="scss" scoped>
.c-template-access-info {
  &__more {
    color: v-bind('themeVars.text2');
  }
}
</style>
